import { useEffect, useMemo } from 'react';
import { ActivityType, useQuery, useQueryClient } from '@common-fe/common-fe';
import { capitalizeFirstLetter } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { api } from '@/api';
import regexp from '@/common/regexp';
import { API_URL, IBankingActivityDetails, IClaimActivityDetails } from '@/dtos';
import {
  ClaimNote,
  FeeModificationPayload,
  NonClaimTransactionData,
  Notification,
  PaymentType,
  PreFormattedDataPayload,
  ProcessingStatusType,
  SectionStyleType,
  Status,
  TransactionApprovalCode,
  TransactionData,
  TransactionStatus,
  TransactionType,
} from '@/modules/transaction/transaction.types';

const formatFeeModificationsResponse = (feeModifications?: FeeModificationPayload[]) =>
  feeModifications?.map((item) => ({
    id: toString(item?.id),
    feeFlowType: item?.fee_flow_type,
    claimId: toString(item?.claim_id),
    amount: toString(item?.amount),
    paidFeeAccountId: toString(item?.paid_fee_account_id),
    paidFeePlanName: item?.paid_fee_plan_name,
    returnedFeeAmountAccountId: toString(item?.returned_fee_amount_account_id),
    returnedFeeAmountPlanName: item?.returned_fee_amount_plan_name,
    transactionId: toString(item?.transaction_id),
    transactionAt: item?.transaction_at,
    date: item.transaction_at,
    nextId: toString(item?.next_id),
    originalFeeId: toString(item?.original_fee_id),
    partnerOrganizationId: toString(item?.partner_organization_id),
    feeType: item?.fee_type,
  }));
const priority: Record<string, number> = {
  [SectionStyleType.ERROR]: 3,
  [SectionStyleType.WARNING]: 2,
  [SectionStyleType.SUCCESS]: 1,
  [SectionStyleType.DEFAULT]: 0,
};
export const formatPreFormattedFeeResponse = (
  activityDetails: PreFormattedDataPayload[] = [],
  statusDetails: PreFormattedDataPayload[] = []
) =>
  [...activityDetails, ...statusDetails]
    .sort((a, b) => (priority[b.section_style_type] ?? 0) - (priority[a.section_style_type] ?? 0))
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .map((item) => ({
      preFormattedActivityType: item?.activity_type,
      htmlText: item?.text,
      date: item?.timestamp,
      sectionStyleType: item?.section_style_type,
      borderStyleType: item?.border_style_type,
    }));

export const GET_TRANSACTION_DETAILS_BY_CLAIM_ID = 'getTransactionDetailsByClaimId';
export const GET_TRANSACTION_DETAILS_BY_POSTING_ID = 'getTransactionDetailsByPostingId';

const formatData = (transaction: IClaimActivityDetails): TransactionData => {
  return {
    claimId: toString(transaction.claim_id),
    documentId: toString(transaction.document_id),
    amount: transaction.amount || 0,
    paidAmount: transaction.paid_amount,
    approvedAmount: transaction.approved_amount,
    category: (transaction.category_type || ActivityType.ADJUSTMENT) as ActivityType,
    id: transaction.claim_id,
    createdAt: transaction.created_at as unknown as string,
    serviceOn: transaction.local_date_service_on as unknown as string,
    invoiceId: transaction.provider_invoice,
    // @ts-ignore
    taxYear: transaction.tax_year || '',
    claimActorDto: {
      dependentId: toString(transaction.claim_actor_dto?.dependent_id),
    },
    notifications:
      transaction.notifications?.map(
        (item): Notification => ({
          approvalCode: item.approval_code as TransactionApprovalCode,
          customDenialReason: item.custom_denial_reason,
          invoicePostingId: `${item.invoice_posting_id || ''}`,
          status: item.status as TransactionStatus,
          validFrom: item.valid_from as unknown as string,
          date: (transaction.statuses?.[0]?.valid_from || item.valid_from) as unknown as string,
          validTo: item.valid_to as unknown as string,
        })
      ) || [],
    claimNoteList:
      transaction.claim_note_dto_list?.map(
        (item) =>
          ({
            ...item,
            // @ts-ignore
            date: item.createdAt as unknown as string,
          }) as ClaimNote
      ) || [],
    ...(transaction.claim_offset_dtos?.length && {
      claimOffsetDonors: transaction.claim_offset_dtos.map((item) => ({
        amount: item.amount || 0,
        donorClaimId: toString(item.donor_claim_id),
        recipientClaimId: toString(item.recipient_claim_id),
        date: item.created_at as unknown as string,
      })),
    }),
    paymentMethodId: toString(transaction.payment_method_id),
    paymentFees:
      transaction.payment_fees?.map((fee) => ({
        amount: toString(fee.amount),
        accountName: fee.account_name,
        accountId: toString(fee.account_id),
        transactionAt: fee.transaction_at as unknown as string,
        date: fee.transaction_at as unknown as string,
        paymentFeeType: fee.payment_fee_type as unknown as string,
        feeId: `${fee.fee_id || ''}`,
      })) || [],
    feeModifications: formatFeeModificationsResponse(
      transaction.fee_modifications as unknown as FeeModificationPayload[]
    ),
    preFormattedActivityDetails: formatPreFormattedFeeResponse(
      transaction.activity_details_v2 as unknown as PreFormattedDataPayload[],
      transaction.status_details as unknown as PreFormattedDataPayload[]
    ),
    v3ActivityDetails: formatPreFormattedFeeResponse(
      transaction.activity_details_v3 as unknown as PreFormattedDataPayload[],
      transaction.status_details as unknown as PreFormattedDataPayload[]
    ),
    employeeNotes: transaction.employee_notes,
    approvalStatus: transaction.notifications?.[0]?.status,
    processingStatus: transaction.processing_status as unknown as ProcessingStatusType,
    processingSubstatusType: transaction.processing_substatus_type as unknown as TransactionApprovalCode,
    memo: `${transaction.memo || ''}`,
    providerId: transaction.provider_id,
    service: {
      id: toString(transaction.service?.id),
      categoryType: transaction.service?.category_type as string,
      // @ts-ignore
      displayed: transaction.service?.displayed || false,
      name: `${transaction.service?.name || ''}`,
    },
    serviceFor: {
      name: `${transaction?.service_for?.name || ''}`,
      birthOn: transaction.service_for?.birth_on as unknown as string,
      relationshipType: capitalizeFirstLetter(transaction.service_for?.relationship_type || 'Me').replace(
        regexp.DASH_SYMBOL,
        ' '
      ),
      isHidden: transaction.service_for?.is_hidden,
      isViewNameAllowed: transaction.service_for?.is_view_name_allowed,
    },
    postings:
      transaction?.postings?.map((item) => ({
        accountId: toString(item.account_id),
        planName: item.plan_name,
        amount: item.amount,
        transactionAt: item.transaction_at as unknown as string,
        date: item.transaction_at as unknown as string,
      })) || [],
    ...(transaction.claim_transfers?.length && {
      claimTransfers: transaction.claim_transfers.map((item) => ({
        id: toString(item.id),
        claimId: toString(item.claim_id),
        accountFromId: toString(item.account_from_id),
        accountFromName: item.account_from_name,
        accountToId: toString(item.account_to_id),
        accountToName: item.account_to_name,
        postingIdFrom: `${item.posting_id_from || ''}`,
        postingIdTo: toString(item.posting_id_to),
        createdByContactId: toString(item.created_by_contact_id),
        createdAt: item.created_at as unknown as string,
        amount: item.amount,
        date: item.created_at as unknown as string,
      })),
    }),
    paymentType: transaction.payment_type as unknown as PaymentType,
    statuses:
      transaction.statuses?.map(
        (item): Status => ({
          status: item.status as TransactionStatus,
          validFrom: item.valid_from as unknown as string,
          date: item.valid_from as unknown as string,
          validTo: item.valid_to as unknown as string,
        })
      ) || [],
    applyDeductibleAmount: transaction.apply_deductible_amount,
    letterOfMedicalNecessities: transaction.letter_of_medical_necessities?.map(
      (letterOfMedicalNecessity) => ({
        id: toString(letterOfMedicalNecessity.id),
        documentId: toString(letterOfMedicalNecessity.document_id),
      })
    ),
  };
};

export const useGetTransactionDetailsByClaimId = (claimId: string) => {
  const { data, isLoading, isFetching, refetch } = useQuery(
    [GET_TRANSACTION_DETAILS_BY_CLAIM_ID, claimId],
    () => api.get(API_URL.ACTIVITIES_CLAIMS_BY_CLAIM_ID(claimId)),
    {
      enabled: !!claimId,
      cacheTime: 2000,
    }
  );
  const queryClient = useQueryClient();
  useEffect(() => {
    if (claimId) {
      refetch();
    } else {
      queryClient.invalidateQueries({
        queryKey: [GET_TRANSACTION_DETAILS_BY_CLAIM_ID],
      });
    }
  }, [claimId, queryClient, refetch]);

  const formattedData = useMemo(() => (data?.data ? formatData(data?.data) : null), [data]);

  return {
    formattedData,
    isLoading,
    isFetching,
    refetch,
  };
};

export const useGetTransactionDetailsByPostingId = (postingId: string, profileId?: string) => {
  const { data, isLoading, isFetching, isSuccess, refetch } = useQuery(
    [GET_TRANSACTION_DETAILS_BY_POSTING_ID, postingId, profileId],
    () => api.get(API_URL.ACTIVITIES_POSTINGS_BY_POSTING_ID(postingId)),
    {
      enabled: !!postingId && !!profileId,
    }
  );
  const queryClient = useQueryClient();
  useEffect(() => {
    if (postingId && profileId) {
      refetch();
    } else {
      queryClient.invalidateQueries({
        queryKey: [GET_TRANSACTION_DETAILS_BY_POSTING_ID],
      });
    }
  }, [postingId, profileId, queryClient, refetch]);
  const formatData = (transaction: IBankingActivityDetails): NonClaimTransactionData => ({
    amount: transaction.amount || 0,
    category: (transaction.category || ActivityType.DEFAULT) as ActivityType,
    employeeId: transaction.employee_id || 0,
    memo: transaction.memo || '',
    planName: transaction.plan_name || '',
    postingId: transaction.posting_id || 0,
    transactionType: transaction.transaction_type as unknown as TransactionType,
    taxYear: transaction?.tax_year,
    paymentFees: transaction?.fees?.map((fee) => ({
      accountId: toString(fee?.account_id),
      accountName: fee?.account_name,
      amount: toString(fee?.amount),
      claimId: toString(fee?.claim_id),
      feeId: toString(fee?.fee_id),
      feeType: fee?.fee_type,
      paymentFeeType: fee?.payment_fee_type,
      policyId: toString(fee?.policy_id),
      postingId: toString(fee?.posting_id),
      transactionAt: fee?.transaction_at as unknown as string,
      date: fee?.transaction_at as unknown as string,
    })),
    feeModifications: formatFeeModificationsResponse(
      transaction?.fee_modifications as unknown as FeeModificationPayload[]
    ),
    preFormattedActivityDetails: formatPreFormattedFeeResponse(
      transaction?.activity_details_v2 as unknown as PreFormattedDataPayload[]
    ),
    statuses: [
      {
        status: transaction?.status,
        validFrom: transaction?.status_date as unknown as string,
        validTo: transaction?.status_date as unknown as string,
      } as Status,
    ],
    statusDate: transaction.status_date as unknown as string,
  });
  const formattedData = useMemo(() => (data?.data ? formatData(data?.data) : null), [data]);

  return {
    formattedData,
    data: data?.data,
    isLoading,
    isFetching,
    isSuccess,
    refetch,
  };
};
